import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { Adapt } from './lib/adapt.js';

// Contrat du rig partagé : interdit. Une fractale plein écran est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'julia',
  family: 'shader',
  danceability: 0.74,
  requiresTier: 1,
  energyLevel: 0.58,
  bpmMin: 60,
  bpmMax: 170,
  movement: 0.60,
  tonality: 0.60,
  enabled: true
};

// JULIA : une fractale de Julia plein écran, z <- z² + c, dont le paramètre
// complexe c voyage avec la musique : la fractale passe en continu des
// spirales aux dendrites, des îles aux éclairs. De l'ambient à la trance,
// par le module adapt.js (lib/adapt.js). Règles R1 à R19.
//
// LE CALCUL (un shader plein écran, highp) : z0 = le pixel, centré sur le
// centre de composition (R19) ; boucle à borne constante (64 itérations au
// tier 2, 48 au tier 1, 32 au tier 0) avec sortie anticipée à |z| > 256.
// - Extérieur : nombre d'itérations FRACTIONNAIRE n + 1 - log2(log|z|), lu
//   dans une palette cosinus tirée de la pochette : aucune bande.
// - Intérieur : « orbit trap », la distance minimale de l'orbite à un cercle
//   et à une croix (mélange qui change à la phrase) : des motifs, jamais un aplat.
// - Bord : estimation de distance d = |z| log|z| / |dz| ; au tier 2 deux
//   échantillons par pixel (décalés en diagonale), au tier 1 un seul et le
//   bord adouci sur 1,5 px par d (R1 : un trait a une largeur en pixels).
// Le rendu va dans une cible privée RGBA8 (demi-résolution aux tiers 0 et 1,
// pleine au tier 2), encodée en gamma (les dégradés sombres ne se baguent pas
// en 8 bits), agrandie en LinearFilter (R2 : la coloration lisse le tolère).
//
// LE CHEMIN DE c, le détail décisif : les formes intéressantes vivent près
// du bord de l'ensemble de Mandelbrot. c longe la cardioïde principale
// c(θ) = e^{iθ}/2 - e^{2iθ}/4, poussée vers l'extérieur le long de sa normale
// de 0,004 (intensité 0 : formes rondes) à 0,075 (intensité 1 : déchiquetée),
// avec des détours vers les bulbes (période 2, 3 en haut et en bas) dont le
// poids monte avec l'intensité. θ oscille dans [0,55 ; 5,73] (jamais par la
// pointe, où la normale n'existe pas) par une somme de deux sinus dont les
// fréquences et les phases viennent de la graine du morceau : même morceau,
// même voyage. θ avance à la vitesse adapt.motion (une phase intégrée, pas
// une distance : R « modulate rate »). Sur les temps, c reçoit une impulsion
// le long de la normale (adapt.pulse, bornée par R9) : la fractale tressaille.
//
// LA PALETTE défile à adapt.motion (les couleurs coulent vers l'intérieur des
// spirales) ; elle change sur la phrase (cutPolicy 'bar'/'phrase'), en fondu
// d'une mesure au calme ('fade'). Registre calme : couleurs profondes ;
// intense : vives.
//
// INTENSITÉ 1 : bulbes et filaments, tressaillement sur chaque temps, ZOOM
// ×4 sur une phrase vers le point fixe répulsif α = (1 - √(1 - 4c)) / 2 (le
// centre des spirales quand c est juste hors de la cardioïde), retour en
// fondu sur la phrase suivante ; séparation RGB du look VDJ sur le temps.
// DROP : c glisse en un temps vers un point très filamenteux pendant 4
// mesures, flash (si permis), palette en opposition (+0,5) ; retour en 2 temps.
//
// Debug : ?juliac=<re>,<im> (c fixe), ?juliapath=1 (le chemin de c sur un
// mini-Mandelbrot en bas à gauche), ?adaptsim=<profil> et ?adaptshow=1 (lib/adapt.js),
// ?juliaprobe=1 (window.__julia).

const TIER = {
  0: { scale: 0.5, iters: 32, samples: 1 },
  1: { scale: 0.5, iters: 48, samples: 1 },
  2: { scale: 1.0, iters: 64, samples: 2 }
};
const BASE_HALF_HEIGHT = 1.12;            // demi-hauteur du plan des z au zoom 1
const PUSH_MIN = 0.004, PUSH_MAX = 0.075;
const KICK = 0.010;                       // impulsion de temps, le long de la normale
const ZOOM_MAX = 4;
const SAFE_FLOOR = 0.5;
const EXPOSURE = 0.92;
const LUM_PERIOD_S = 0.5;
const PATH_POINTS = 48;
// des points très filamenteux (dendrites, éclairs) pour le drop
const DROP_TARGETS = [[0.0, 1.0], [-0.1011, 0.9563], [0.285, 0.0135], [-0.7457, 0.1102], [-1.7548, 0.0], [0.3245, 0.0425]];
// les bulbes visités (centre, rayon)
const BULBS = [[-1.0, 0.0, 0.25], [-0.1226, 0.7449, 0.0947], [-0.1226, -0.7449, 0.0947]];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function smoothstep(a, b, x) { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
function hashSeed(str) { let h = 2166136261 >>> 0; const t = String(str || ''); for (let i = 0; i < t.length; i++) { h ^= t.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h || 1; }
function rng(seed) { let s = (seed >>> 0) || 1; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

// ------------------------------------------------------------ le chemin (sans GPU, testé)
// la cardioïde, sa normale extérieure (unitaire)
export function cardioid(theta) {
  const x = 0.5 * Math.cos(theta) - 0.25 * Math.cos(2 * theta);
  const y = 0.5 * Math.sin(theta) - 0.25 * Math.sin(2 * theta);
  const tx = -0.5 * Math.sin(theta) + 0.5 * Math.sin(2 * theta);
  const ty = 0.5 * Math.cos(theta) - 0.5 * Math.cos(2 * theta);
  const l = Math.hypot(tx, ty) || 1;
  return { x, y, nx: ty / l, ny: -tx / l };
}
// les paramètres du voyage d'un morceau
export function pathParams(seed) {
  const r = rng(seed * 2654435761 + 17);
  return { w1: 0.55 + 0.5 * r(), w2: 1.3 + 0.9 * r(), p1: r() * 6.283, p2: r() * 6.283, a2: 0.25 + 0.2 * r(),
    wb: 0.21 + 0.15 * r(), pb: r() * 6.283, bulb0: Math.floor(r() * BULBS.length), drop0: Math.floor(r() * DROP_TARGETS.length) };
}
// c pour la phase s (intégrée à adapt.motion) et l'intensité I ; renvoie aussi la normale
export function pathC(s, I, P) {
  const u = Math.sin(s * P.w1 + P.p1) * (1 - P.a2) + Math.sin(s * P.w2 + P.p2) * P.a2;   // -1..1
  const theta = Math.PI + 2.59 * u;                                   // [0,55 ; 5,73]
  const k = cardioid(theta);
  const push = lerp(PUSH_MIN, PUSH_MAX, Math.pow(clamp01(I), 1.5));
  let cx = k.x + k.nx * push, cy = k.y + k.ny * push;
  // LES DÉTOURS : vers un bulbe qui change lentement, d'autant plus que l'intensité est haute
  const wave = 0.5 - 0.5 * Math.cos(s * P.wb + P.pb);
  const w = smoothstep(0.35, 1.0, I) * smoothstep(0.25, 0.9, wave) * 0.85;
  if (w > 0) {
    const bi = (P.bulb0 + Math.floor((s * P.wb + P.pb) / 6.283)) % BULBS.length;
    const b = BULBS[(bi + BULBS.length) % BULBS.length];
    const phi = s * 0.9 + P.p2;
    const rr = b[2] + push * 0.8;
    cx = lerp(cx, b[0] + rr * Math.cos(phi), w);
    cy = lerp(cy, b[1] + rr * Math.sin(phi), w);
  }
  return { x: cx, y: cy, nx: k.nx, ny: k.ny };
}
// le point fixe répulsif α = (1 - √(1 - 4c)) / 2 : le centre des spirales
export function alphaFixed(cx, cy) {
  const ax = 1 - 4 * cx, ay = -4 * cy;
  const m = Math.hypot(ax, ay);
  const sx = Math.sqrt((m + ax) / 2), sy = Math.sign(ay || 1) * Math.sqrt(Math.max(0, (m - ax) / 2));
  return { x: (1 - sx) / 2, y: -sy / 2 };
}

// ------------------------------------------------------------ GLSL
const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

function buildFragmentShader(L, debugPath) {
  return `
  precision highp float;
  uniform vec2 uC;
  uniform vec2 uCenter;          // le centre de la vue dans le plan des z
  uniform float uHalf;           // demi-hauteur de la vue dans le plan des z
  uniform float uAspect;
  uniform vec2 uRes;             // taille de la cible, en pixels
  uniform vec3 uPalA, uPalB, uPalC, uPalD;
  uniform vec3 uPalA2, uPalB2, uPalC2, uPalD2;
  uniform float uPalMix;
  uniform float uShift;
  uniform float uFreq;           // cycles de palette par itération
  uniform float uTrapMix;        // 0 cercle, 1 croix
  uniform float uTrapR;
  uniform float uExposure;
  uniform float uDeep;           // 1 au calme : couleurs profondes
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  ${debugPath ? `uniform vec2 uPath[${PATH_POINTS}];` : ''}
  varying vec2 vUv;

  vec3 pal(float t) {
    vec3 a = mix(uPalA, uPalA2, uPalMix), b = mix(uPalB, uPalB2, uPalMix), c = mix(uPalC, uPalC2, uPalMix), d = mix(uPalD, uPalD2, uPalMix);
    return clamp(a + b * cos(6.2831853 * (c * t + d)), 0.0, 1.0);
  }
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  // une orbite : la couleur d'un point du plan des z
  vec3 julia(vec2 z, float pxZ) {
    vec2 dz = vec2(1.0, 0.0);
    float trapC = 1e3, trapX = 1e3;
    float n = 0.0;
    float r2 = 0.0;
    bool esc = false;
    for (int i = 0; i < ${L.iters}; i++) {
      dz = 2.0 * vec2(z.x * dz.x - z.y * dz.y, z.x * dz.y + z.y * dz.x);
      z = vec2(z.x * z.x - z.y * z.y, 2.0 * z.x * z.y) + uC;
      r2 = dot(z, z);
      trapC = min(trapC, abs(sqrt(r2) - uTrapR));
      trapX = min(trapX, min(abs(z.x), abs(z.y)));
      if (r2 > 65536.0) { esc = true; break; }   // |z| > 256 : la formule lisse est alors continue à 1e-3 près (à 16, des marches restaient visibles)
      n += 1.0;
    }
    if (esc) {
      // l'itération fractionnaire : aucune bande
      float lz = 0.5 * log(r2);
      float mu = n + 1.0 - log2(lz);
      vec3 col = pal(mu * uFreq - uShift);
      // le bord : la distance estimée, en pixels ; le voisinage du bord s'éclaircit en fil (R1)
      float de = 0.5 * sqrt(r2) * lz / max(length(dz), 1e-20);
      float dpx = de / pxZ;
      // sombre loin de l'ensemble, lumineux en s'en approchant : le fond reste noir
      // profond, les anneaux de couleur montent vers les filaments
      float b = 1.0 - exp(-mu * 0.16);
      col *= mix(0.04, 1.0, b * b) * mix(1.0, 0.72, uDeep);
      ${L.samples === 1 ? 'col = mix(pal(0.5 - uShift) * 0.9, col, smoothstep(0.0, 1.5, dpx));' : ''}
      return col;
    }
    // l'intérieur : l'orbit trap, des motifs (jamais un aplat)
    // deux lectures du piège : des anneaux de couleur (la distance, en cycles de
    // palette) et un fil lumineux là où l'orbite frôle le cercle ou la croix
    float trap = mix(trapC, trapX, uTrapMix);
    float glow = exp(-trap * 9.0);
    vec3 col = pal(0.35 + trap * 4.5 + uShift * 0.5);
    float rings = 0.5 + 0.5 * cos(trap * 40.0 - uShift * 6.2831853);
    return col * (0.08 + 0.55 * glow + 0.25 * rings * (1.0 - glow)) * mix(1.0, 0.75, uDeep);
  }

  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec2 p = vec2(ndc.x * uAspect, ndc.y);
    float pxZ = 2.0 * uHalf / uRes.y;                    // un pixel de la cible, en unités de z
    vec2 z0 = uCenter + p * uHalf;
    ${L.samples === 2
      ? 'vec3 col = 0.5 * (julia(z0 + vec2(0.25, 0.25) * pxZ, pxZ) + julia(z0 - vec2(0.25, 0.25) * pxZ, pxZ));'
      : 'vec3 col = julia(z0, pxZ);'}
    ${debugPath ? `
    // ?juliapath=1 : le mini-Mandelbrot (bas gauche), le chemin passé de c, c en blanc
    vec2 box = vec2(-uAspect + 0.06, -0.94);
    vec2 bs = vec2(0.62, 0.50);
    vec2 q = (p - box) / bs;
    if (q.x >= 0.0 && q.x <= 1.0 && q.y >= 0.0 && q.y <= 1.0) {
      vec2 c = vec2(-2.1 + q.x * 2.7, -1.1 + q.y * 2.2);
      vec2 w = vec2(0.0); float k = 0.0;
      for (int i = 0; i < 40; i++) { w = vec2(w.x * w.x - w.y * w.y, 2.0 * w.x * w.y) + c; if (dot(w, w) > 4.0) break; k += 1.0; }
      col = k >= 40.0 ? vec3(0.02) : vec3(0.10 + 0.25 * k / 40.0);
      float px = 2.7 / (bs.x * uRes.y * 0.5);
      for (int i = 0; i < ${PATH_POINTS}; i++) { float d = length(uPath[i] - c) / px; col = mix(col, vec3(0.2, 0.8, 1.0), (1.0 - smoothstep(2.0, 3.5, d)) * (0.35 + 0.65 * float(i) / ${PATH_POINTS}.0)); }
      float dc = length(uC - c) / px;
      col = mix(col, vec3(1.0), 1.0 - smoothstep(1.5, 3.0, dc));
    }` : ''}
    col *= uExposure * safeAtt(vUv);
    // encodé en gamma dans la cible RGBA8 (décodé à l'affichage)
    gl_FragColor = vec4(pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2)), 1.0);
  }
  `;
}

const displayFragmentShader = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(pow(texture2D(uTex, vUv).rgb, vec3(2.2)), 1.0); }
`;
const lumFragmentShader = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() {
    vec3 c = pow(texture2D(uTex, vUv).rgb, vec3(2.2));
    float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
    gl_FragColor = vec4(vec3(pow(l, 1.0 / 2.2)), 1.0);
  }
`;

// une compilation par tier (et par mode debug), partagée entre instances : le Pi met des secondes à compiler
const MATERIALS = new Map();

// =============================================================================

export class JuliaVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const fc = (urlParam('juliac') || '').split(',').map(parseFloat);
    this._forceC = fc.length === 2 && fc.every(Number.isFinite) ? fc : null;
    this._debugPath = urlParam('juliapath') === '1';
    // le look VDJ : la séparation RGB pulsée au temps (à l'intensité haute), bloom faible
    this._post = new VdjPost({ bloomStrength: 0.35, bloomThreshold: 0.82, grain: 0.012, vignette: 0.45, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._postValue = this._post.update(0);
    this._comfort = new VdjComfort();
    if (urlParam('juliaprobe') === '1' && typeof window !== 'undefined') window.__julia = this;
  }

  get postProcessing() { return this._postValue; }

  init(scene) {
    this._scene = scene;
    const L = this._layout;
    const key = this._tier + (this._debugPath ? ':path' : '');
    let mat = MATERIALS.get(key);
    if (!mat) {
      const v3 = () => ({ value: new THREE.Vector3() });
      const uniforms = {
        uC: { value: new THREE.Vector2(-0.8, 0.156) }, uCenter: { value: new THREE.Vector2() }, uHalf: { value: BASE_HALF_HEIGHT },
        uAspect: { value: 1.78 }, uRes: { value: new THREE.Vector2(960, 540) },
        uPalA: v3(), uPalB: v3(), uPalC: v3(), uPalD: v3(), uPalA2: v3(), uPalB2: v3(), uPalC2: v3(), uPalD2: v3(),
        uPalMix: { value: 0 }, uShift: { value: 0 }, uFreq: { value: 0.05 }, uTrapMix: { value: 0 }, uTrapR: { value: 0.6 },
        uExposure: { value: EXPOSURE }, uDeep: { value: 1 },
        uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
        uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
      };
      if (this._debugPath) uniforms.uPath = { value: Array.from({ length: PATH_POINTS }, () => new THREE.Vector2(9, 9)) };
      mat = new THREE.ShaderMaterial({ uniforms, vertexShader: quadVertexShader, fragmentShader: buildFragmentShader(L, this._debugPath), depthTest: false, depthWrite: false });
      MATERIALS.set(key, mat);
    }
    this._mat = mat; this._u = mat.uniforms;

    this._target = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false });
    this._fScene = new THREE.Scene();
    this._fQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    this._fQuad.frustumCulled = false;
    this._fScene.add(this._fQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._dispU = { uTex: { value: this._target.texture } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: quadVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    this._display.onBeforeRender = (renderer) => this._render(renderer);
    scene.add(this._display);
    // la mesure de confort (R10) : 16 x 16 texels relus deux fois par seconde
    this._lumTarget = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false });
    this._lumPixels = new Uint8Array(16 * 16 * 4);
    this._lumU = { uTex: { value: this._target.texture } };
    this._lumScene = new THREE.Scene();
    const lq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._lumU, vertexShader: quadVertexShader, fragmentShader: lumFragmentShader, depthTest: false, depthWrite: false }));
    lq.frustumCulled = false; this._lumScene.add(lq);
    this._lumAge = 0; this._lumMeasured = 0.25; this._lumPending = false;
    this._size = new THREE.Vector2();

    this._adapt = new Adapt(); this._a = this._adapt.get();
    this._paused = false; this._dropLeft = 0; this._lumAvg = 0.2; this._gain = 1;
    this._time = 0; this._s = 0; this._shift = 0; this._zoom = 1; this._phraseN = 0;
    this._seedSeen = null; this._P = pathParams(1);
    this._drop = 0; this._dropTarget = DROP_TARGETS[0]; this._dropWas = false;
    this._c = { x: -0.8, y: 0.156 }; this._cPrev = null; this._maxStep = 0;
    this._paletteSeen = undefined; this._palIdx = 0; this._palFade = 1; this._palFadeRate = 0;
    this._baseHues = [0.85, 0.5];
    this._path = [];
    this._trapMix = 0; this._trapTarget = 0;
    this._setPalette(this._u, 'uPal', 0); this._setPalette(this._u, 'uPal', 0, '2');
    this._stats = { beats: 0, phrases: 0, drops: 0, flashes: 0, paletteChanges: 0 };
  }

  // ------------------------------------------------------------ la palette
  _applyCover(profile) {
    const pal = profile.artworkPalette;
    if (pal === this._paletteSeen) return;
    this._paletteSeen = pal;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2bd6, 0x2bd8ff];
    const hsl = { h: 0, s: 0, l: 0 };
    const h1 = vdjColor(v[0]).getHSL(hsl).h;
    let h2 = vdjColor(v[1 % v.length]).getHSL(hsl).h;
    // R4 : deux couleurs à distinguer ont 0,25 de teinte d'écart au moins
    let dh = ((h2 - h1) % 1 + 1) % 1; if (dh > 0.5) dh -= 1;
    if (Math.abs(dh) < 0.25) h2 = h1 + (dh >= 0 ? 0.33 : -0.33);
    this._baseHues = [h1, h2];
    this._setPalette(this._u, 'uPal', this._palIdx, this._palFade < 1 ? '2' : '');
    this._setPalette(this._u, 'uPal', this._palIdx, '2');
  }
  // une palette cosinus psychédélique autour des deux teintes de la pochette ;
  // idx fait tourner les variantes (fréquences, déphasages) à chaque phrase
  _setPalette(U, name, idx, suffix) {
    const sx = suffix || '';
    const [h1, h2] = this._baseHues;
    const r = rng(1013 + idx * 7919);
    // la saturation d'adapt (0,65 profond .. 0,9 vif) : amplitude de la palette autour d'un niveau sombre
    const sat = this._a ? this._a.saturation : 0.65;
    const lvl = lerp(0.34, 0.48, (sat - 0.65) / 0.25), amp = lerp(0.40, 0.54, (sat - 0.65) / 0.25);
    U[name + 'A' + sx].value.set(lvl, lvl, lvl);
    U[name + 'B' + sx].value.set(amp, amp, amp);
    const f = 0.8 + 0.6 * r();
    U[name + 'C' + sx].value.set(f, f * (0.9 + 0.3 * r()), f * (1.1 + 0.3 * r()));
    // les déphasages : la teinte 1 au début du cycle, la teinte 2 au milieu (saturation forte : canaux très déphasés)
    const base = idx % 2 ? h2 : h1;
    U[name + 'D' + sx].value.set(base, base + 0.33 + 0.1 * r(), base + 0.67 - 0.1 * r());
    if (this._drop > 0.5) U[name + 'D' + sx].value.addScalar(0.5);
  }

  // ------------------------------------------------------------ la boucle
  update(deltaTime, profile) {
    // pause : adapt gèle (dt = 0), le rendu garde la dernière image
    this._paused = profile.playbackState != null && profile.playbackState !== 'PLAYING';
    if (this._paused) { this._adapt.update(0, profile); return; }
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const a = this._a = this._adapt.update(dt, profile);
    this._time += dt;
    this._applyCover(profile);
    // la graine : le morceau (ou le profil simulé) - même morceau, même voyage
    const seed = hashSeed(this._adapt.simulated ? 'sim:' + this._adapt.simulated : (profile.trackId || ((profile.title || '') + '|' + (profile.artist || ''))));
    if (seed !== this._seedSeen) { this._seedSeen = seed; this._P = pathParams(seed); this._s = 0; this._path = []; }
    const I = a.intensity;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || (this._adapt.simulated ? 120 : 110);
    const beatS = 60 / bpm;

    // le voyage : la phase intègre adapt.motion (vitesse, jamais distance)
    this._s += dt * a.motion * 0.16;
    // la palette défile : les couleurs coulent vers l'intérieur
    this._shift += dt * a.motion * 0.09;

    // PHRASE : nouvelle palette (coupe, ou fondu d'une mesure au calme), nouveau piège
    if (a.phrase) {
      this._phraseN++; this._stats.phrases++;
      const fade = a.cutPolicy === 'fade';
      if (!fade || this._phraseN % 2 === 0) {
        this._palIdx++;
        // l'ancienne passe en palette 2 (mélange 0 = ancienne), la nouvelle arrive
        this._copyPalette('', '2');
        this._setPalette(this._u, 'uPal', this._palIdx, '');
        this._palFade = 0;
        this._palFadeRate = fade ? 1 / (4 * beatS) : 1e3;   // une mesure, ou une coupe
        this._stats.paletteChanges++;
      }
      this._trapTarget = this._trapTarget > 0.5 ? 0 : 1;
    }
    this._palFade = Math.min(1, this._palFade + dt * this._palFadeRate);
    this._u.uPalMix.value = 1 - this._palFade;          // 1 = palette 2 (l'ancienne), 0 = la nouvelle
    this._trapMix += (this._trapTarget - this._trapMix) * (1 - Math.exp(-dt / 2.0));

    // DROP : c glisse vers un point filamenteux pendant 4 mesures, flash, palette en opposition
    if (a.drop && a.dropReady) {
      this._stats.drops++;
      this._dropLeft = 16;
      this._dropTarget = DROP_TARGETS[(this._P.drop0 + this._stats.drops) % DROP_TARGETS.length];
      const director = profile.director;
      // R9/R10 : un flash seulement quand la musique coupe sur la mesure (dansabilité et grille hautes)
      if (director && a.cutPolicy === 'bar' && this._comfort.requestFlash(this._time)) { director.flash = VDJ_DROP_FLASH; this._stats.flashes++; }
    }
    // 4 mesures : c glisse en un temps vers la cible, revient en 2 temps
    const dropOn = this._dropLeft > 0;
    if (dropOn) { this._dropLeft -= dt / beatS; this._drop = Math.min(1, this._drop + dt / beatS); }
    else this._drop = Math.max(0, this._drop - dt / beatS / 2);
    if ((this._drop > 0.5) !== this._dropWas) {
      this._dropWas = this._drop > 0.5;
      // l'opposition de palette : sur les deux palettes, en coupe (c'est le drop)
      this._u.uPalD.value.addScalar(0.5); this._u.uPalD2.value.addScalar(0.5);
    }

    // c : le chemin, l'impulsion du temps, le drop
    const pc = pathC(this._s, I, this._P);
    // le tressaillement : l'amplitude permise par adapt (intensité x percussivité x grille),
    // l'enveloppe du temps (70 ms) ; sans grille, l'enveloppe d'attaque d'adapt
    const env = Math.exp(-a.beatPhase * beatS / 0.07);
    const kick = KICK * Math.max(a.pulse * env, 0.6 * a.pulse * a.onset) * smoothstep(0.25, 0.9, I);
    let cx = pc.x + pc.nx * kick, cy = pc.y + pc.ny * kick;
    const dw = smoothstep(0, 1, this._drop);
    cx = lerp(cx, this._dropTarget[0], dw); cy = lerp(cy, this._dropTarget[1], dw);
    if (this._forceC) { cx = this._forceC[0]; cy = this._forceC[1]; }
    if (this._cPrev) {
      const st = Math.hypot(cx - this._cPrev.x, cy - this._cPrev.y);
      this._maxStep = Math.max(this._maxStep, st);
      if (this._drop === 0) this._maxStepCalm = Math.max(this._maxStepCalm || 0, st);
    }
    this._cPrev = { x: cx, y: cy };
    this._c = { x: cx, y: cy };
    if (a.beat) this._stats.beats++;

    // LE ZOOM : à haute intensité, x4 sur une phrase vers α, puis retour sur la suivante
    let zt = 1;
    if (I > 0.7 && !this._forceC) {
      const up = this._phraseN % 2 === 0;
      // la position dans la phrase : comptée en temps depuis la dernière phrase
      this._phraseBeats = a.phrase ? 0 : (this._phraseBeats || 0) + dt / beatS;
      const u = smoothstep(0, 1, Math.min(1, this._phraseBeats / 16));
      zt = Math.pow(ZOOM_MAX, up ? u : 1 - u);
      zt = lerp(1, zt, smoothstep(0.7, 0.85, I));
    }
    this._zoom += (zt - this._zoom) * (1 - Math.exp(-dt / 0.6));
    const al = alphaFixed(cx, cy);
    const zf = 1 - 1 / this._zoom;                        // 0 au zoom 1 : centré sur le centre de composition (R19)
    const U = this._u;
    U.uCenter.value.set(al.x * zf, al.y * zf);
    U.uHalf.value = BASE_HALF_HEIGHT / this._zoom;
    U.uC.value.set(cx, cy);
    U.uAspect.value = safeZoneUniforms.uAspect.value || 1.78;
    U.uShift.value = this._shift % 1000;
    U.uFreq.value = lerp(0.09, 0.16, I);
    U.uTrapMix.value = this._trapMix;
    U.uTrapR.value = 0.45 + 0.25 * Math.sin(this._s * 0.7);
    U.uDeep.value = clamp01((0.9 - a.saturation) / 0.25);

    if (this._debugPath) {
      this._pathAge = (this._pathAge || 0) + dt;
      if (this._pathAge > 0.25) { this._pathAge = 0; this._path.push([cx, cy]); if (this._path.length > PATH_POINTS) this._path.shift(); }
      const arr = U.uPath.value;
      for (let i = 0; i < PATH_POINTS; i++) { const q = this._path[i - (PATH_POINTS - this._path.length)]; if (q) arr[i].set(q[0], q[1]); else arr[i].set(9, 9); }
    }

    // R10 : la luminance moyenne mesurée, ramenée sous le plafond
    this._lumAge += dt;
    if (this._lumAge >= LUM_PERIOD_S) { this._lumAge = 0; this._lumPending = true; }
    // le plafond suit l'intensité (adapt.lumaCap, 0,25 au calme .. 0,45 intense)
    // (gain ABSOLU : plafond / luminance mesurée sans gain - un gain re-multiplié à
    // chaque image s'effondrait à 0,3 pendant que la moyenne lente restait haute)
    this._lumAvg += (this._lumMeasured - this._lumAvg) * Math.min(1, dt / 1.0);
    const cap = 0.9 * a.lumaCap;
    const gt = Math.min(1, cap / Math.max(1e-3, this._lumAvg));
    this._gain += (gt - this._gain) * Math.min(1, dt / 0.5);
    U.uExposure.value = EXPOSURE * this._gain;
    // la séparation RGB du look VDJ, sur le temps, à l'intensité haute seulement
    const chroma = smoothstep(0.55, 0.95, I) * vdjBeatChroma(a.beatPhase, beatS);
    this._postValue = this._post.update(chroma);
  }

  _copyPalette(from, to) {
    for (const k of ['A', 'B', 'C', 'D']) this._u['uPal' + k + to].value.copy(this._u['uPal' + k + from].value);
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    if (prev) this._size.set(prev.width, prev.height); else renderer.getDrawingBufferSize(this._size);
    const s = this._layout.scale;
    const w = Math.max(2, Math.round(this._size.x * s)), h = Math.max(2, Math.round(this._size.y * s));
    if (this._target.width !== w || this._target.height !== h) this._target.setSize(w, h);
    this._u.uRes.value.set(w, h);
    if (!this._paused || !this._rendered) {
      this._gainRendered = this._gain;
      renderer.setRenderTarget(this._target);
      renderer.render(this._fScene, this._ortho);
      this._rendered = true;
    }
    if (this._lumPending) {
      this._lumPending = false;
      renderer.setRenderTarget(this._lumTarget);
      renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, 16, 16, this._lumPixels);
      let sum = 0;
      for (let i = 0; i < this._lumPixels.length; i += 4) sum += this._lumPixels[i];
      // en valeurs affichées (sRGB), comme R10 et VdjComfort ; ramenée au gain 1
      // (la cible est rendue avec le gain courant)
      this._lumMeasured = sum / (256 * 255) / Math.max(0.05, this._gainRendered || 1);
    }
    renderer.setRenderTarget(prev);
  }

  getProbe() {
    return { tier: this._tier, c: [+this._c.x.toFixed(4), +this._c.y.toFixed(4)], zoom: +this._zoom.toFixed(3), s: +this._s.toFixed(3), maxStepC: +this._maxStep.toFixed(5), maxStepCHorsDrop: +(this._maxStepCalm || 0).toFixed(5),
      drop: +this._drop.toFixed(2), lum: +this._lumMeasured.toFixed(3), lumAvg: +this._lumAvg.toFixed(3), gain: +this._gain.toFixed(3), stats: Object.assign({}, this._stats),
      adapt: { intensity: +this._a.intensity.toFixed(3), motion: +this._a.motion.toFixed(3), pulse: +this._a.pulse.toFixed(3), cutPolicy: this._a.cutPolicy, colorMode: this._a.colorMode, saturation: +this._a.saturation.toFixed(3), lumaCap: +this._a.lumaCap.toFixed(3), dropReady: this._a.dropReady, sim: this._adapt.simulated } };
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._fQuad) this._fQuad.geometry.dispose();
    if (this._target) this._target.dispose();
    if (this._lumTarget) this._lumTarget.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__julia === this) window.__julia = null;
    this._scene = null;
  }
}
